import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users, ChevronRight, BookOpen, Heart, MessageCircle, Shield, Sparkles, UserPlus } from 'lucide-react';

const FRIENDSHIP_CONTENT = [
  {
    id: 'find',
    title: 'Hitta nya vänner',
    icon: UserPlus,
    description: 'Praktiska tips för att skapa nya vänskaper.',
    content: [
      '**Det är normalt att det är svårt:** Som vuxen är det svårare att skaffa vänner – du är inte ensam om det.',
      '**Gå dit folk är:** Hobbykurser, föreningar, gym, bokcirklar – regelbundenhet skapar relationer.',
      '**Var den som tar initiativ:** Bjud hem, föreslå aktiviteter, följ upp efter träffar.',
      '**Sänk ribban:** Alla vänner behöver inte bli livslånga – det är okej med "aktivitetsvänner".',
      '**Använd appar:** Det finns appar för vänskapsdating som Bumble BFF.',
      '**Återknyt kontakter:** Hör av dig till gamla vänner du tappat kontakten med.',
      '**Var tålmodig:** Vänskap tar tid – det krävs många möten för att bygga tillit.'
    ],
    questions: [
      'Vad hindrar dig från att ta kontakt med nya människor?',
      'Vilka intressen har du som du kan utforska tillsammans med andra?',
      'Finns det gamla vänner du skulle vilja återknyta med?',
      'Hur ofta tar du initiativ till att ses?'
    ]
  },
  {
    id: 'maintain',
    title: 'Vårda vänskap',
    icon: Heart,
    description: 'Hur du håller vänskaper vid liv.',
    content: [
      '**Regelbunden kontakt:** Schemalägg om du måste – en snabb "tänker på dig" räcker långt.',
      '**Var närvarande:** När ni ses, lägg bort telefonen och lyssna på riktigt.',
      '**Minns detaljerna:** Fråga hur det gick med jobbet, hunden, eller det du pratade om sist.',
      '**Fira och stötta:** Var där i både framgångar och motgångar.',
      '**Var ärlig:** Säg om något stör dig innan det blir en stor grej.',
      '**Acceptera förändring:** Vänskap utvecklas – det är okej att relationen ser annorlunda ut.',
      '**Prioritera:** Du kan inte vara nära vän med alla. Välj vilka relationer du vill investera i.'
    ],
    questions: [
      'Vilka vänner prioriterar du?',
      'Hur ofta hör du av dig till dina närmaste vänner?',
      'Finns det någon vänskap du försummat?',
      'Hur visar du dina vänner uppskattning?'
    ]
  },
  {
    id: 'quality',
    title: 'Vännskap vs bekanta',
    icon: Users,
    description: 'Förstå skillnaden och acceptera båda.',
    content: [
      '**Alla relationer har värde:** Ytliga bekantskaper fyller också en funktion.',
      '**Nära vänner (2-5):** Dem du kan ringa mitt i natten, som känner till allt.',
      '**Goda vänner (5-15):** Dem du umgås med regelbundet och delar mycket med.',
      '**Bekanta (50+):** Dem du träffar i specifika sammanhang och har ytliga samtal med.',
      '**Förväntningar:** Problem uppstår när vi förväntar oss "nära vän"-beteende av bekanta.',
      '**Utveckling:** En bekant kan bli en nära vän över tid – men det kräver investering från båda.'
    ],
    questions: [
      'Hur många nära vänner har du egentligen?',
      'Förväntar du dig för mycket av bekanta?',
      'Vilka vänner vill du fördjupa relationen med?'
    ]
  },
  {
    id: 'difficult',
    title: 'Svåra situationer',
    icon: Shield,
    description: 'Hantera konflikter och växande isär.',
    content: [
      '**Konflikter händer:** Även bästa vänner bråkar ibland – det behöver inte betyda slutet.',
      '**Prata om det:** Undvik inte elefanten i rummet. Säg hur du känner.',
      '**Acceptera att folk förändras:** Ni kanske växer åt olika håll – det är naturligt.',
      '**Ensidig vänskap:** Om du alltid är den som hör av sig, kanske det är dags att släppa taget.',
      '**Avsluta med värdighet:** Ibland är det bäst att låta en vänskap tona ut utan drama.',
      '**Sorg är okej:** Att förlora en vän gör ont – tillåt dig själv att sörja.'
    ],
    questions: [
      'Finns det en konflikt du behöver ta tag i?',
      'Har du vänner som bara tar och aldrig ger?',
      'Har du förlorat en vänskap som du fortfarande sörjer?'
    ]
  },
  {
    id: 'be-friend',
    title: 'Var en bra vän',
    icon: Sparkles,
    description: 'Egenskaper som gör dig till en fantastisk vän.',
    content: [
      '**Lyssna mer än du pratar:** Visa genuint intresse för deras liv.',
      '**Håll hemligheter:** Förtroende är grunden för nära vänskap.',
      '**Var pålitlig:** Gör det du säger att du ska göra.',
      '**Acceptera dem som de är:** Försök inte förändra dina vänner.',
      '**Var generös:** Med tid, uppmärksamhet och ibland praktisk hjälp.',
      '**Erkänn misstag:** Be om ursäkt när du har fel.',
      '**Heja på dem:** Var genuint glad för deras framgångar.',
      '**Respektera gränser:** Var inte klistrande eller krävande.'
    ],
    questions: [
      'Vilken typ av vän är du?',
      'Vad uppskattar dina vänner mest hos dig?',
      'Finns det något du skulle kunna förbättra?'
    ]
  }
];

export default function FriendshipPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="friendship-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Vänskap</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till vänskapsverktyg.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = FRIENDSHIP_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="friendship-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Vänskap</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-pink-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-pink-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="friendship-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Vänskap</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <Users className="w-12 h-12 mx-auto text-pink-500" />
          <h2 className="font-heading text-2xl font-semibold">Vänskap</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Hitta, vårda och underhåll meningsfulla vänskaper genom livet.
          </p>
        </div>

        <div className="space-y-3">
          {FRIENDSHIP_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        <div className="bg-pink-50 rounded-2xl p-4 border border-pink-100">
          <p className="text-pink-800 text-sm">
            💡 <strong>Visste du?</strong> Forskning visar att starka vänskaper är lika viktiga för hälsan som motion och bra kost.
          </p>
        </div>
      </main>
    </div>
  );
}
